import { PermissionStatus } from '@backrs_ui/common-ui';

import { createApi } from '@reduxjs/toolkit/query/react';
import type { WritableDraft } from 'immer';

import { USER_PROFILE_FIELDS } from './profile.constants';
import { baseQueryWithAuth } from '../baseQuery';

type ProfilePermissions = Readonly<{
  edit_profile: PermissionStatus;
  view_profile: PermissionStatus;
  access_team: PermissionStatus;
  crud_user: PermissionStatus;
  direct_message: PermissionStatus;
  meet: PermissionStatus;
  invest: PermissionStatus;
  back: PermissionStatus;
  propose: PermissionStatus;
  read_user: PermissionStatus;
  view_full_name: PermissionStatus;
}>;

type AutocompleteResultItem = Record<string, string>;

type AutocompleteResponse = Readonly<{
  results: AutocompleteResultItem[];
}>;

// Define the return type for the autocomplete query
export type AutocompleteResult = Readonly<{
  endpoint: string;
  values: {
    label: string;
    value: string;
  }[];
  searchTerm?: string;
}>;

export type ProfileValueOption = Readonly<{
  id: number;
  name: string;
  value?: string;
}>;

export type StoryDocument = Readonly<{
  id: number;
  user_id: number;
  category_id: string;
  question_id: string;
  option_id: 'resume' | 'transcript' | 'other' | 'purpose_video' | 'personal_assessment' | null;
  profile_option_answers_id: number;
  title: string;
  description: string;
  visibility: string;
  media_id: number;
  media_type: 'text' | 'document' | 'video' | 'pdf' | 'docx' | 'image';
  media_url: string;
  uri?: string;

  // LinkedInJobsData-specific fields
  job_title?: string;
  company_name?: string;
  dates?: string;
  created_at?: string;
  updated_at?: string;
  source?: string | null;
  school_name?: string;
  certification_received?: string;
  skill?: string;
}>;

export type LinkedInUrlData = Readonly<{
  id: number;
  user_id: number;
  value: string;
  option_id?: string | null;
  suboption_id?: string | null;
  created_at?: string;
  updated_at?: string;
  source?: string | null;
  category_id: string;
  question_id: string;
}>;

export type AcademicEnrollmentType = 'high_school' | 'post_secondary' | 'none';

export type ProfileValueOptionResponse = Readonly<{
  academicMetrics: {
    sat_score?: ProfileValueOption;
    act_score?: ProfileValueOption;
    high_school_gpa?: ProfileValueOption;
    post_secondary_gpa?: ProfileValueOption;
  };
  high_school_gpa?: ProfileValueOption;
  post_secondary_gpa?: ProfileValueOption;

  major_gpa?: ProfileValueOption;
  sat_score?: ProfileValueOption;
  act_score?: ProfileValueOption;

  academicEnrollmentTypes: (ProfileValueOption & {
    level: AcademicEnrollmentType;
  })[];
  academicsHighSchoolCurrentEnrollment: ProfileValueOption[];
  academicsPostSecondaryCurrentEnrollment: ProfileValueOption[];
  academicHighSchoolExpectedGraduationYear: ProfileValueOption[];
  academicPostSecondaryExpectedGraduationYear: ProfileValueOption[];
  academicsAlmaMaters: { id: number; school: string }[];

  academicApTests: ProfileValueOption[];
  academicEntranceExams: ProfileValueOption[];

  academicExtracurricularInvolvement: ProfileValueOption[];
  academicRigor: ProfileValueOption[];
  academicDegrees: ProfileValueOption[];
  academicMajors: (ProfileValueOption & { major_gpa: number; academic_majors_id: number })[];
  academicEducationalExperiences: ProfileValueOption[];

  academicsToggleHighSchool: ProfileValueOption[];
  academicsTogglePostSecondary: ProfileValueOption[];
  academicsTogglePostGraduate: ProfileValueOption[];

  demographicsFamilyRoles: ProfileValueOption[];
  demographicsChildhoodExperiences: ProfileValueOption[];
  demographicsRelationshipExperiences: ProfileValueOption[];
  demographicsReligions: ProfileValueOption[];
  demographicsGenders: ProfileValueOption[];
  demographicsEthnicities: ProfileValueOption[];
  demographicsAffinities: ProfileValueOption[];
  demographicsMyersBriggs: ProfileValueOption[];
  demographicsHigh5s: ProfileValueOption[];
  demographicsStrengthsFinders: ProfileValueOption[];
  demographicsAstrologySigns: ProfileValueOption[];

  calendlyData: {
    calendly_url?: LinkedInUrlData[];
  };

  careerExperiences: ProfileValueOption[];
  careerOccupation: ProfileValueOption[];
  careerPaths: ProfileValueOption[];
  careerEmployer: ProfileValueOption[];

  financialProfile: ProfileValueOption[];
  financialFamilyIncomes: ProfileValueOption[];
  financialCredentials: ProfileValueOption[];
  financialExperiences: ProfileValueOption[];

  healthExperiences: ProfileValueOption[];
  healthInterests: ProfileValueOption[];
  healthMentalHealthInterests: ProfileValueOption[];
  healthFoodNutrition: ProfileValueOption[];

  hobbiesCreativeInterests: ProfileValueOption[];
  hobbiesPassions: ProfileValueOption[];
  hobbiesFavorites: ProfileValueOption[];

  resourcesClubs: { id: number; club: string }[];
  resourcesLanguages: { id: number; language: string }[];

  documentsResumes: StoryDocument[];
  documentsTranscripts: StoryDocument[];
  documentsPersonalAssessment: StoryDocument[];
  documentsOther: StoryDocument[];

  documentsStoryVideos: StoryDocument[];
  documentsStoryVideosGroup: StoryDocument[];
  documentsPurposeVideo: StoryDocument[];

  profileImage: { id: number; name: string }[];
  profileBannerImage: { id: number; name: string }[];

  linkedInData: {
    linkedIn_url?: LinkedInUrlData[];
    linkedIn_jobs?: StoryDocument[];
    linkedIn_education?: StoryDocument[];
    linkedIn_skills?: StoryDocument[];
  };

  publicProfile: {
    id?: number;
    first_name?: string;
    last_name?: string;
    profile_image?: string;
    profile_banner?: string;
    city?: string;
    state?: string;
  };
}>;

export type ProfileFields = keyof ProfileValueOptionResponse;

type UserProfileTestScore = Readonly<{
  id: number;
  user_academics_id: number;
  score: number;
  created_at: string;
  updated_at: string;
}>;

type UserProfileApTest = UserProfileTestScore & {
  ap_test_id: number;
};

type UserProfileAcademicMajor = Readonly<{
  id: number;
  user_academics_id: number;
  academic_majors_id: number;
  major_gpa: number;
  created_at: string;
  updated_at: string;
}>;

type UserProfileEntranceExam = UserProfileTestScore & {
  entrance_exam_id: number;
};

export type UserProfileResponse = Readonly<{
  profile: {
    academicMetrics?: {
      high_school_gpa?: number;
      post_secondary_gpa?: number;
      sat_score?: number;
      act_score?: number;
    };

    academicEnrollmentTypes?: number;
    academicHighSchoolExpectedGraduationYear?: number;
    academicPostSecondaryExpectedGraduationYear?: number;
    academicsHighSchoolCurrentEnrollment?:
      | string
      | {
          id: number;
          school: string;
        };

    academicsPostSecondaryCurrentEnrollment?:
      | string
      | {
          id: number;
          school: string;
        };

    academicsAlmaMaters?: ({ id: number; school: string } | string)[];

    academicApTests?: UserProfileApTest[];
    academicEntranceExams?: UserProfileEntranceExam[];

    academicExtracurricularInvolvement?: number;
    academicRigor?: number;
    academicDegrees?: number;
    academicMajors?: UserProfileAcademicMajor[];
    academicEducationalExperiences?: number[];

    careerOccupation?: string;
    careerExperiences?: number[];
    careerPaths?: number[];
    careerEmployer?: string[];

    demographicsGenders?: number;
    demographicsEthnicities?: number;
    demographicsReligions?: number;
    demographicsAffinities?: number[];
    demographicsRelationshipExperiences?: number[];
    demographicsFamilyRoles?: number[];
    demographicsChildhoodExperiences: number[];
    demographicsMyersBriggs: number;
    demographicsHigh5s: number[];
    demographicsStrengthsFinders: number[];
    demographicsAstrologySigns: number;

    financialExperiences?: number[];
    financialCredentials?: number[];
    financialProfile?: number[];
    financialFamilyIncomes: number;

    healthMentalHealthInterests: ({ id: number; name: string } | string)[];
    healthInterests?: ({ id: number; name: string } | string)[];
    healthFoodNutrition?: ({ id: number; name: string } | string)[];
    healthExperiences?: number[];

    hobbiesCreativeInterests?: number[];
    hobbiesPassions?: number[];
    hobbiesFavorites?: number[];

    resourcesClubs?: string[];
    resourcesLanguages?: ({ id: number; language: string } | string)[];

    documentsResumes?: StoryDocument[];
    documentsTranscripts?: StoryDocument[];
    documentsPersonalAssessment?: StoryDocument[];
    documentsOther?: StoryDocument[];

    documentsStoryVideos?: StoryDocument[];
    documentsPurposeVideo?: StoryDocument[];

    linkedInData?: {
      linkedIn_url?: LinkedInUrlData[];
      linkedIn_jobs?: StoryDocument[];
      linkedIn_education?: StoryDocument[];
      linkedIn_skills?: StoryDocument[];
    };

    calendlyData?: {
      calendly_url?: LinkedInUrlData[];
    };

    publicProfile: {
      id?: number;
      first_name?: string;
      last_name?: string;
      profile_image?: string;
      profile_banner_image?: string;
      role: 'student' | 'professional';
      city?: string;
      state?: string;
      grade?: string;
    };
  };
}>;

type UpdateProfileRequest = Readonly<{
  field: ProfileFields;
  value:
    | number
    | string
    | (number | string)[]
    | null
    | Omit<StoryDocument, 'id' | 'media_url' | 'profile_option_answers_id'>
    | {
        gpa?: number;
        sat_score?: number;
        act_score?: number;
      };
}>;

export const profileApi = createApi({
  reducerPath: 'profileApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['UserProfile'],
  endpoints: (builder) => ({
    fetchProfileValueOptions: builder.query<ProfileValueOptionResponse, undefined>({
      query: () => 'profile-value-options',
    }),
    fetchUserProfileInfo: builder.query<UserProfileResponse, ProfileFields[]>({
      query: () => ({
        url: 'user-profile',
        method: 'GET',
      }),
      providesTags: ['UserProfile'],
    }),

    // Endpoint for fetching profile permissions for a user
    fetchProfilePermissions: builder.query<ProfilePermissions, { userId: string | undefined }>({
      query: ({ userId }) => {
        if (!userId) {
          throw new Error('userId is required');
        }

        return {
          url: `users/profile/permissions/${userId}`,
          method: 'GET',
        };
      },
    }),

    // New endpoint for fetching public profile
    fetchPublicProfileInfo: builder.query<UserProfileResponse, { userId: string | undefined }>({
      query: ({ userId }) => {
        if (!userId) {
          throw new Error('userId is required');
        }

        return {
          url: `user-profile/public/${userId}`,
          method: 'GET',
        };
      },
    }),

    fetchAutocompleteOptions: builder.query<
      AutocompleteResult[],
      { endpoint?: string; searchTerm: string }
    >({
      query: ({ endpoint, searchTerm }) => {
        if (!searchTerm || searchTerm.length < 4 || !endpoint) {
          throw new Error('Search term must be at least 4 characters');
        }

        return {
          url: endpoint,
          method: 'GET',
          params: { q: searchTerm },
        };
      },
      transformResponse: (response: AutocompleteResponse, _meta, { endpoint }) => {
        // Extract all values into a single array
        const values = response.results
          .map((item) => {
            // Extract the first non-empty value from the item
            for (const key in item) {
              if (item[key]) {
                return { label: item[key], value: item[key] };
              }
            }

            return { label: '', value: '' };
          })
          .filter(Boolean);

        if (!endpoint) {
          throw new Error('Endpoint is required');
        }

        return [{ endpoint, values }];
      },
    }),

    updateUserProfile: builder.mutation<UserProfileResponse, UpdateProfileRequest>({
      query: ({ field, value }) => ({
        url: 'user-profile',
        method: 'PATCH',
        body: { [field]: value },
      }),
      async onQueryStarted(_, { queryFulfilled, dispatch }) {
        try {
          const { data: updated } = await queryFulfilled;

          if (!updated?.profile) return;

          dispatch(
            profileApi.util.updateQueryData(
              'fetchUserProfileInfo',
              USER_PROFILE_FIELDS,
              (draft: WritableDraft<UserProfileResponse>) => {
                draft.profile = {
                  ...draft.profile,
                  ...updated.profile,
                };
              },
            ),
          );
        } catch {
          console.error('error updating field');
        }
      },
    }),
  }),
});

export const {
  useFetchProfileValueOptionsQuery,
  useFetchUserProfileInfoQuery,
  useFetchPublicProfileInfoQuery,
  useFetchProfilePermissionsQuery,
  useFetchAutocompleteOptionsQuery,
  useUpdateUserProfileMutation,
  util: { resetApiState },
} = profileApi;
